import React, {useEffect, useState} from 'react'
import {Button, CircularProgress, Stack, Typography} from "@mui/material"
import {Link as RouterLink} from "react-router-dom"
import {ProjectJson} from "../../api/json/ProjectJson"
import {AnyCustomModelJson} from "../../api/json/CustomModelJson"
import {CustomModelRequests} from "../../api/requests/CustomModelRequests"
import {ProjectRequests} from "../../api/requests/ProjectRequests"
import CustomModelSelect from "../models/CustomModelSelect"
import CodeBlock from "../common/CodeBlock"
import ModalBase from "../modal/ModalBase"
import InfoAlert from "../alert/InfoAlert"
import AsyncButton from "../button/AsyncButton"
import ErrorMessage from "../error/ErrorMessage"
import ApiError from "../../api/ApiError"
import API from "../../api/API"

interface ProjectApiTabProps {
    project: ProjectJson
    didUpdate: (project: ProjectJson) => void
}

function ProjectApiTab({project, didUpdate}: ProjectApiTabProps) {
    const [models, setModels] = useState<AnyCustomModelJson[]>()
    const [loadError, setLoadError] = useState<ApiError>()

    const [pendingModel, setPendingModel] = useState<AnyCustomModelJson | null>(null)
    const [switching, setSwitching] = useState(false)
    const [switchError, setSwitchError] = useState<ApiError>()

    useEffect(() => {
        CustomModelRequests.list(project.id, undefined, undefined, true)
            .then(paged => {
                setModels(paged.content)
                setLoadError(undefined)
            })
            .catch(setLoadError)
    }, [project.id])

    const defaultModel = models?.find(model => model.id === project.defaultCustomModelId)

    const handleConfirmSwitch = () => {
        if (!pendingModel) return
        setSwitching(true)
        ProjectRequests.setDefaultCustomModel(project.id, pendingModel.id)
            .then(updated => {
                didUpdate(updated)
                setPendingModel(null)
            })
            .catch(setSwitchError)
            .finally(() => setSwitching(false))
    }

    const endpoint = `${API.getBaseUrl()}/api/v1/projects/${project.id}/generations`
    const inputExample = Object.fromEntries(project.columns.map(column => [column.reference, "..."]))
    const inputJson = JSON.stringify({input: inputExample})

    const curl = [
        `curl -X POST "${endpoint}" \\`,
        `  -H "Authorization: Bearer ep_live_YOUR_API_KEY" \\`,
        `  -H "Content-Type: application/json" \\`,
        `  -d '${inputJson}'`,
    ].join("\n")

    const js = [
        `const response = await fetch("${endpoint}", {`,
        `  method: "POST",`,
        `  headers: {`,
        `    "Authorization": "Bearer ep_live_YOUR_API_KEY",`,
        `    "Content-Type": "application/json",`,
        `  },`,
        `  body: JSON.stringify(${inputJson}),`,
        `})`,
        `const {id, output} = await response.json()`,
    ].join("\n")

    return (
        <Stack spacing={4} sx={{maxWidth: 720}}>
            <Stack spacing={2}>
                <Typography variant="subtitle1">Default model</Typography>
                {loadError && <ErrorMessage error={loadError}/>}
                {!models && !loadError && <CircularProgress/>}
                {models && models.length === 0 &&
                    <Typography variant="body2" color="text.secondary">
                        Train and finish a custom model to set it as the API default.
                    </Typography>
                }
                {models && models.length > 0 &&
                    <>
                        <Typography variant="body2" color="text.secondary">
                            The API generates with this model. Switch it here without changing your API calls.
                        </Typography>
                        <CustomModelSelect models={models}
                                           selected={defaultModel}
                                           onSelect={model => {
                                               if (model.id !== project.defaultCustomModelId) {
                                                   setSwitchError(undefined)
                                                   setPendingModel(model)
                                               }
                                           }}
                                           sx={{maxWidth: 360}}/>
                    </>
                }
            </Stack>

            <Stack spacing={2}>
                <Typography variant="subtitle1">Usage</Typography>
                {!defaultModel &&
                    <Typography variant="body2" color="text.secondary">
                        Set a default model above before generating via the API.
                    </Typography>
                }
                <Typography variant="body2" color="text.secondary">
                    Authenticate with an API key. Inputs are keyed by column reference.
                </Typography>
                <Button component={RouterLink}
                        to="/organization?tab=api-keys"
                        size="small"
                        sx={{alignSelf: "flex-start"}}>
                    Manage API keys
                </Button>
                <CodeBlock code={curl}/>
                <CodeBlock code={js}/>
            </Stack>

            <ModalBase open={pendingModel !== null}
                       setOpen={open => { if (!open) setPendingModel(null) }}
                       title="Change default model"
                       error={switchError}
                       buttons={
                           <>
                               <Button variant="outlined" color="secondary" disabled={switching}
                                       onClick={() => setPendingModel(null)}>Cancel</Button>
                               <AsyncButton waiting={switching} onClick={handleConfirmSwitch}>Switch model</AsyncButton>
                           </>
                       }>
                <InfoAlert>
                    This model backs your public API. Switching takes effect immediately for live API calls.
                </InfoAlert>
            </ModalBase>
        </Stack>
    )
}

export default ProjectApiTab
